import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../lib/api';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  Bell, Shield, Battery, Package, AlertTriangle, CheckCircle,
  XCircle, Clock, ArrowRight, RefreshCw, Settings, Mail, Smartphone,
  Users, TrendingUp, Calendar
} from 'lucide-react';

export default function AlertsDashboardPage() {
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      const res = await api.get('/alerts/dashboard');
      setData(res.data);
    } catch {
      toast.error('Eroare la incarcarea dashboard-ului de alerte');
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  if (loading) return (
    <div className="flex items-center justify-center h-64">
      <RefreshCw className="w-6 h-6 animate-spin text-slate-400" />
    </div>
  );

  if (!data) return null;

  const { iscir, battery, stock, summary } = data;

  return (
    <div className="space-y-6" data-testid="alerts-dashboard">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-3" data-testid="alerts-dashboard-title">
            <Bell className="w-7 h-7 text-amber-500" /> Dashboard Alerte
          </h1>
          <p className="text-sm text-slate-500 mt-1">Vizualizare centralizata a tuturor alertelor din sistem</p>
        </div>
        <Button variant="outline" size="sm" onClick={fetchData} data-testid="refresh-alerts-btn">
          <RefreshCw className="w-4 h-4 mr-1.5" /> Actualizeaza
        </Button>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="alerts-summary">
        <div className={`p-5 rounded-xl border-2 ${summary.total_critical > 0 ? 'border-red-300 bg-red-50' : 'border-slate-200 bg-slate-50'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-red-600">Critice</p>
              <p className="text-3xl font-bold text-red-700 mt-1" data-testid="critical-count">{summary.total_critical}</p>
              <p className="text-xs text-red-500 mt-1">Expirate / Intarziate / Fara service</p>
            </div>
            <XCircle className="w-10 h-10 text-red-300" />
          </div>
        </div>
        <div className={`p-5 rounded-xl border-2 ${summary.total_warning > 0 ? 'border-amber-300 bg-amber-50' : 'border-slate-200 bg-slate-50'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-amber-600">Avertismente</p>
              <p className="text-3xl font-bold text-amber-700 mt-1" data-testid="warning-count">{summary.total_warning}</p>
              <p className="text-xs text-amber-500 mt-1">Expira in curand / Scadente</p>
            </div>
            <AlertTriangle className="w-10 h-10 text-amber-300" />
          </div>
        </div>
        <div className={`p-5 rounded-xl border-2 ${summary.total_low_stock > 0 ? 'border-blue-300 bg-blue-50' : 'border-slate-200 bg-slate-50'}`}>
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs font-semibold uppercase tracking-wider text-blue-600">Stoc Minim</p>
              <p className="text-3xl font-bold text-blue-700 mt-1" data-testid="low-stock-count">{summary.total_low_stock}</p>
              <p className="text-xs text-blue-500 mt-1">Articole sub stoc minim</p>
            </div>
            <Package className="w-10 h-10 text-blue-300" />
          </div>
        </div>
      </div>

      {/* Detail Sections */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* ISCIR Section */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="iscir-alerts-section">
          <div className="p-4 border-b bg-gradient-to-r from-violet-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-violet-600" />
                <h2 className="font-semibold text-slate-800">Alerte ISCIR</h2>
              </div>
              <div className="flex items-center gap-2">
                {iscir.settings_enabled ? (
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Activ</Badge>
                ) : (
                  <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-xs">Inactiv</Badge>
                )}
              </div>
            </div>
            {iscir.recipients_count > 0 && (
              <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                <Users className="w-3 h-3" /> {iscir.recipients_count} destinatari
              </div>
            )}
          </div>
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <StatBox label="Expirate" value={iscir.expired} variant="danger" />
              <StatBox label="Expira in 7 zile" value={iscir.expiring_7d} variant="warning" />
              <StatBox label="Expira in 30 zile" value={iscir.expiring_30d} variant="info" />
              <StatBox label="Expira in 90 zile" value={iscir.expiring_90d} variant="neutral" />
            </div>
            {/* Recent logs */}
            {iscir.recent_logs.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Ultimele notificari</p>
                <div className="space-y-1.5">
                  {iscir.recent_logs.map((log, i) => (
                    <div key={i} className="flex items-center justify-between text-xs p-2 bg-slate-50 rounded">
                      <span className="text-slate-600">{log.date || log.created_at?.slice(0, 10)}</span>
                      <span className="text-slate-500">{log.alerts_count} alerte</span>
                      <div className="flex items-center gap-1.5">
                        {log.email_sent > 0 && <span className="flex items-center gap-0.5 text-emerald-600"><Mail className="w-3 h-3" />{log.email_sent}</span>}
                        {log.sms_sent > 0 && <span className="flex items-center gap-0.5 text-blue-600"><Smartphone className="w-3 h-3" />{log.sms_sent}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Button variant="outline" size="sm" className="w-full text-xs mt-2" onClick={() => navigate('/iscir')} data-testid="go-iscir-btn">
              <ArrowRight className="w-3 h-3 mr-1" /> Deschide Conformitate ISCIR
            </Button>
          </div>
        </div>

        {/* Battery Section */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="battery-alerts-section">
          <div className="p-4 border-b bg-gradient-to-r from-cyan-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Battery className="w-5 h-5 text-cyan-600" />
                <h2 className="font-semibold text-slate-800">Alerte Service Baterii</h2>
              </div>
              <div className="flex items-center gap-2">
                {battery.settings_enabled ? (
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Activ</Badge>
                ) : (
                  <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-xs">Inactiv</Badge>
                )}
              </div>
            </div>
            {battery.recipients_count > 0 && (
              <div className="flex items-center gap-1 mt-2 text-xs text-slate-500">
                <Users className="w-3 h-3" /> {battery.recipients_count} destinatari
              </div>
            )}
          </div>
          <div className="p-4 space-y-3">
            <div className="grid grid-cols-2 gap-2">
              <StatBox label="Intarziate" value={battery.overdue} variant="danger" />
              <StatBox label="Scadente" value={battery.upcoming} variant="warning" />
              <StatBox label="Fara service" value={battery.no_service} variant="info" />
              <StatBox label="La zi" value={battery.ok} variant="success" />
            </div>
            <div className="text-xs text-slate-500 text-center py-1">
              Total echipamente: <span className="font-semibold">{battery.total_equipment}</span>
            </div>
            {/* Recent logs */}
            {battery.recent_logs.length > 0 && (
              <div className="mt-3">
                <p className="text-xs font-semibold text-slate-500 uppercase mb-2">Ultimele notificari</p>
                <div className="space-y-1.5">
                  {battery.recent_logs.map((log, i) => (
                    <div key={i} className="flex items-center justify-between text-xs p-2 bg-slate-50 rounded">
                      <span className="text-slate-600">{log.date || log.created_at?.slice(0, 10)}</span>
                      <span className="text-slate-500">{log.alerts_count} alerte</span>
                      <div className="flex items-center gap-1.5">
                        {log.email_sent > 0 && <span className="flex items-center gap-0.5 text-emerald-600"><Mail className="w-3 h-3" />{log.email_sent}</span>}
                        {log.sms_sent > 0 && <span className="flex items-center gap-0.5 text-blue-600"><Smartphone className="w-3 h-3" />{log.sms_sent}</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
            <Button variant="outline" size="sm" className="w-full text-xs mt-2" onClick={() => navigate('/maintenance/battery-service')} data-testid="go-battery-btn">
              <ArrowRight className="w-3 h-3 mr-1" /> Deschide Service Baterii
            </Button>
          </div>
        </div>

        {/* Stock Section */}
        <div className="bg-white rounded-xl border shadow-sm overflow-hidden" data-testid="stock-alerts-section">
          <div className="p-4 border-b bg-gradient-to-r from-amber-50 to-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Package className="w-5 h-5 text-amber-600" />
                <h2 className="font-semibold text-slate-800">Alerte Stoc Minim</h2>
              </div>
              <div className="flex items-center gap-2">
                {stock.settings_enabled ? (
                  <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-xs">Activ</Badge>
                ) : (
                  <Badge className="bg-slate-100 text-slate-500 border-slate-200 text-xs">Inactiv</Badge>
                )}
              </div>
            </div>
          </div>
          <div className="p-4 space-y-3">
            <div className="text-center py-2">
              <p className="text-3xl font-bold text-amber-600" data-testid="stock-alert-count">{stock.low_stock_count}</p>
              <p className="text-xs text-slate-500 mt-1">articole sub stoc minim</p>
            </div>
            {stock.low_stock_items.length > 0 && (
              <div className="space-y-1">
                {stock.low_stock_items.slice(0, 6).map((item, i) => (
                  <div key={i} className="flex items-center justify-between text-xs p-2 bg-slate-50 rounded">
                    <div className="flex-1 truncate">
                      <span className="font-medium text-slate-700">{item.code}</span>
                      <span className="text-slate-400 ml-1.5">{item.name?.slice(0, 30)}</span>
                    </div>
                    <div className="flex items-center gap-2 ml-2">
                      <span className="text-red-600 font-medium">{item.current_stock}</span>
                      <span className="text-slate-400">/</span>
                      <span className="text-slate-500">{item.min_stock}</span>
                      <span className="text-slate-400 text-[10px]">{item.unit}</span>
                    </div>
                  </div>
                ))}
                {stock.low_stock_items.length > 6 && (
                  <p className="text-xs text-slate-400 text-center">+{stock.low_stock_items.length - 6} articole</p>
                )}
              </div>
            )}
            <Button variant="outline" size="sm" className="w-full text-xs mt-2" onClick={() => navigate('/wms/stock-alerts')} data-testid="go-stock-alerts-btn">
              <ArrowRight className="w-3 h-3 mr-1" /> Deschide Alerte Stoc
            </Button>
          </div>
        </div>
      </div>

      {/* Quick Actions */}
      <div className="bg-white rounded-xl border shadow-sm p-4" data-testid="quick-actions">
        <h3 className="text-sm font-semibold text-slate-600 mb-3 flex items-center gap-2">
          <Settings className="w-4 h-4" /> Configurare rapida
        </h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Button variant="outline" size="sm" className="h-auto py-3 flex-col gap-1.5" onClick={() => navigate('/iscir')} data-testid="config-iscir-btn">
            <Shield className="w-5 h-5 text-violet-500" />
            <span className="text-xs">Setari ISCIR</span>
          </Button>
          <Button variant="outline" size="sm" className="h-auto py-3 flex-col gap-1.5" onClick={() => navigate('/maintenance/battery-service')} data-testid="config-battery-btn">
            <Battery className="w-5 h-5 text-cyan-500" />
            <span className="text-xs">Setari Baterii</span>
          </Button>
          <Button variant="outline" size="sm" className="h-auto py-3 flex-col gap-1.5" onClick={() => navigate('/admin/mail')} data-testid="config-mail-btn">
            <Mail className="w-5 h-5 text-blue-500" />
            <span className="text-xs">Config. Email</span>
          </Button>
          <Button variant="outline" size="sm" className="h-auto py-3 flex-col gap-1.5" onClick={() => navigate('/admin/alerts')} data-testid="config-alerts-btn">
            <Bell className="w-5 h-5 text-amber-500" />
            <span className="text-xs">Alerte Automate</span>
          </Button>
        </div>
      </div>
    </div>
  );
}

function StatBox({ label, value, variant }) {
  const styles = {
    danger: 'bg-red-50 border-red-200 text-red-700',
    warning: 'bg-amber-50 border-amber-200 text-amber-700',
    info: 'bg-blue-50 border-blue-200 text-blue-700',
    success: 'bg-emerald-50 border-emerald-200 text-emerald-700',
    neutral: 'bg-slate-50 border-slate-200 text-slate-600',
  };
  return (
    <div className={`p-2.5 rounded-lg border text-center ${styles[variant] || styles.neutral}`}>
      <p className="text-xl font-bold">{value}</p>
      <p className="text-[10px] font-medium mt-0.5">{label}</p>
    </div>
  );
}
